import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Calendar, Power } from 'lucide-react';
import FEATURE_KEYS from '../../../../constants/feature-keys';
import type { Facility } from '../../utils/utils';
import { isFacilityDeactivated } from '../../utils/utils';
import DeactivatedBadge from '../cells/DeactivatedBadge';
import FacilityIdentityCell from '../cells/FacilityIdentityCell';
import AdminStatusCell from '../cells/AdminStatusCell';
import LicenseCell from '../cells/LicenseCell';
import type {
    FacilityColumn,
    StageFilterOption,
    StageSortOption,
} from '../utils/facilityTableTypes';

interface CreateColumnsOptions {
    onDeactivate?: (facility: Facility) => void;
}

export const activeFilterOptions: StageFilterOption[] = [
    { value: 'all', label: 'All' },
    { value: 'deactivated', label: 'Deactivated' },
];

export const activeSortOptions: StageSortOption[] = [];

export function createActiveColumns({
    onDeactivate,
}: CreateColumnsOptions): FacilityColumn[] {
    return [
        {
            key: 'facility',
            header: 'Facility',
            className: 'w-[20%]',
            cell: (f) => <FacilityIdentityCell facility={f} />,
        },
        {
            key: 'since',
            header: 'Active Since',
            className: 'w-[12%]',
            cell: (f) => (
                <div className="flex items-center gap-1.5">
                    <Calendar
                        className="h-3 w-3 text-muted-foreground"
                        aria-hidden="true"
                    />
                    <span className="text-xs text-foreground">
                        {f.start_date
                            ? new Date(f.start_date).toLocaleDateString(
                                  undefined,
                                  {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                  }
                              )
                            : '—'}
                    </span>
                </div>
            ),
        },
        {
            key: 'users',
            header: 'Users',
            className: 'w-[10%]',
            cell: (f) => <LicenseCell facility={f} />,
        },
        {
            key: 'value-realization',
            header: 'Value realization',
            className: 'w-[14%]',
            cell: (f) => (
                <ValueRealizationCell score={f.valueRealizationScore} />
            ),
        },
        {
            key: 'cpt',
            header: 'CPT',
            className: 'w-[8%]',
            cell: (f) => (
                <span className="text-xs tabular-nums text-muted-foreground">
                    {f.featureAccess?.includes(FEATURE_KEYS.cptReporting)
                        ? (f.reportCount ?? 0)
                        : '—'}
                </span>
            ),
        },
        {
            key: 'admin',
            header: 'Site Admin',
            className: 'w-[14%]',
            cell: (f) => <AdminStatusCell facility={f} showPending={false} />,
        },
        {
            key: 'features',
            header: 'Features',
            className: 'w-[12%]',
            cell: (f) => (
                <div className="flex flex-wrap gap-1">
                    {f.featureAccess?.length ? (
                        f.featureAccess.map((feat) => (
                            <Badge
                                key={feat}
                                variant="outline"
                                className="rounded-full border-white/10 bg-white/5 px-2 py-0.5 text-xs font-medium text-muted-foreground"
                            >
                                {feat}
                            </Badge>
                        ))
                    ) : (
                        <span className="text-xs text-muted-foreground/50">
                            —
                        </span>
                    )}
                </div>
            ),
        },
        {
            key: 'actions',
            header: '',
            className: 'w-[10%] text-right',
            headerClassName: 'text-right',
            cell: (f) => (
                <div className="flex justify-end">
                    {isFacilityDeactivated(f) ? (
                        <DeactivatedBadge />
                    ) : (
                        onDeactivate && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDeactivate(f);
                                }}
                            >
                                <Power
                                    className="mr-1 h-3 w-3"
                                    aria-hidden="true"
                                />
                                Deactivate
                            </Button>
                        )
                    )}
                </div>
            ),
        },
    ];
}

function ValueRealizationCell({ score }: { score?: number | null }) {
    if (score == null) {
        return <span className="text-xs text-muted-foreground/50">—</span>;
    }

    const clampedScore = Math.max(0, Math.min(score, 100));

    return (
        <div className="min-w-20">
            <span className="text-xs font-medium tabular-nums text-foreground">
                {clampedScore}%
            </span>
            <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-white/5">
                <div
                    className="h-full rounded-full bg-zinc-300"
                    style={{ width: `${clampedScore}%` }}
                />
            </div>
        </div>
    );
}
